import { API_BASE_URL } from "../config/apiConfig";
import { createAuthAxios, handleApiError } from "../utils/apiUtils";

/**
 * 🚀 INDUSTRY-STANDARD 1-1 MESSAGING SERVICE
 *
 * Features:
 * - Multi-layer caching (Redis + Memory)
 * - Cursor-based pagination
 * - Permission-based conversations
 * - Real-time delivery & read receipts
 * - Optimized unread count tracking
 * - Advanced typing indicators
 * - Message search capabilities
 * - Batch operations for better performance
 */

const MESSAGING_BASE = `${API_BASE_URL}/messaging`;

const messagingService = {
  // ============================================
  // PRESENCE & ONLINE STATUS (NEW)
  // ============================================

  /**
   * Get online contacts with their status
   * NEW: Uses the new /online-contacts endpoint
   *
   * @returns {Promise<Object>} Online and offline contacts with presence data
   */
  getOnlineContacts: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(`${MESSAGING_BASE}/online-contacts`);

      return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  },

  /**
   * Get conversations with real-time presence information
   * NEW: Uses the new /conversations-with-presence endpoint
   *
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.limit - Items per page (default: 20)
   * @returns {Promise<Object>} Conversations with presence data
   */
  getConversationsWithPresence: async (options = {}) => {
    try {
      const { page = 1, limit = 20 } = options;

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${MESSAGING_BASE}/conversations-with-presence`,
        {
          params: { page, limit },
        }
      );

      return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  },

  /**
   * Send presence heartbeat to keep online status active
   * NEW: Should be called every 30 seconds
   *
   * @param {string} status - Status: 'online', 'away', 'busy', 'invisible'
   * @returns {Promise<Object>} Heartbeat confirmation
   */
  sendPresenceHeartbeat: async (status = "online") => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${MESSAGING_BASE}/presence/heartbeat`,
        { status }
      );

      return response.data;
    } catch (error) {
      // Silent fail for heartbeat - don't disrupt user experience
return null;
    }
  },

  // ============================================
  // CONTACTS MANAGEMENT
  // ============================================

  /**
   * Get available contacts for messaging (followers + collaborators)
   * Combines followers and collaborators into one list
   *
   * @param {Object} options - Query options
   * @param {number} options.page - Page number
   * @param {number} options.limit - Items per page
   * @param {string} options.search - Search query
   * @returns {Promise<Object>} Available contacts
   */
  getAvailableContacts: async (options = {}) => {
    try {
      const { page = 1, limit = 50, search = "" } = options;
      const authAxios = createAuthAxios();

      // Build query parameters
      const params = { page, limit };
      if (search) {
        params.search = search;
      }

      // Use the new dedicated backend endpoint
      const response = await authAxios.get(
        `${MESSAGING_BASE}/available-contacts`,
        { params }
      );

      return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  },

  // ============================================
  // CONVERSATION MANAGEMENT
  // ============================================

  /**
   * Start a new conversation with a user
   * Requires: Follow/Collaboration or existing conversation
   *
   * @param {string} participantId - User ID to start conversation with
   * @returns {Promise<Object>} Conversation data with otherParticipant info
   */
  startConversation: async (participantId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(`${MESSAGING_BASE}/conversations`, {
        participantId: participantId,
      });

      return response.data;
    } catch (error) {
      // Handle permission errors
      if (error.response?.status === 403) {
throw new Error(
          "You must follow or have collaborated with this user to start a conversation"
        );
      }
      throw handleApiError(error);
    }
  },

  /**
   * Get all conversations for the current user
   * Uses multi-layer caching for optimal performance
   *
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.limit - Items per page (default: 20)
   * @param {boolean} options.includeArchived - Include archived conversations
   * @returns {Promise<Object>} Conversations with pagination
   */
  getConversations: async (options = {}) => {
    try {
      const { page = 1, limit = 20, includeArchived = false } = options;

      const authAxios = createAuthAxios();
      const response = await authAxios.get(`${MESSAGING_BASE}/conversations`, {
        params: {
          page,
          limit,
          includeArchived,
        },
      });

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get a specific conversation details
   *
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Conversation details
   */
  getConversationDetails: async (conversationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${MESSAGING_BASE}/conversations/${conversationId}`
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ============================================
  // MESSAGE MANAGEMENT (Cursor Pagination)
  // ============================================

  /**
   * Get messages for a conversation with cursor-based pagination
   * Industry standard for infinite scrolling
   *
   * @param {string} conversationId - Conversation ID
   * @param {Object} options - Query options
   * @param {string} options.cursor - Cursor for pagination (null for initial load)
   * @param {number} options.limit - Number of messages to fetch (default: 50)
   * @returns {Promise<Object>} Messages with pagination cursors
   */
  getMessages: async (conversationId, options = {}) => {
    try {
      const { cursor = null, limit = 50 } = options;

      const params = { limit };
      if (cursor) {
        params.cursor = cursor;
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${MESSAGING_BASE}/conversations/${conversationId}/messages`,
        { params }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Send a new message to a conversation
   * Rate limited: 40 msgs/min per user, 20 msgs/10s per conversation
   *
   * @param {string} conversationId - Conversation ID
   * @param {Object} messageData - Message data
   * @param {string} messageData.content - Message content (plaintext or encrypted)
   * @param {string} messageData.messageType - Message type ('text', 'audio', etc.)
   * @param {Object} messageData.encryptedContent - Encrypted package (E2EE mode)
   * @param {boolean} messageData.isEncrypted - Whether message is encrypted
   * @returns {Promise<Object>} Sent message with server-generated ID
   */
  sendMessage: async (conversationId, messageData) => {
    try {
      const authAxios = createAuthAxios();

      // Build request payload
      const payload = {
        messageType: messageData.messageType || "text",
      };

      // E2EE mode: Send encrypted package
      if (messageData.encryptedContent) {
        payload.encryptedContent = messageData.encryptedContent;
        payload.isEncrypted = true;
      }
      // Plain text mode: Send content directly
      else {
        payload.content = messageData.content.trim();
      }

      // ✅ Add replyTo if present
      if (messageData.replyTo) {
        payload.replyTo = messageData.replyTo;
      }

      const response = await authAxios.post(
        `${MESSAGING_BASE}/conversations/${conversationId}/messages`,
        payload
      );

      return response.data;
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error("You cannot send messages to this user");
      } else if (error.response?.status === 429) {
        throw new Error(
          "You are sending messages too quickly. Please slow down."
        );
      }
      throw handleApiError(error);
    }
  },

  // ============================================
  // UNREAD COUNT MANAGEMENT
  // ============================================

  /**
   * Get total unread count across all conversations
   * Optimized with caching for < 50ms latency
   *
   * @returns {Promise<Object>} Total unread count and breakdown by conversation
   */
  getUnreadCount: async () => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.get(`${MESSAGING_BASE}/unread-count`);

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ============================================
  // READ RECEIPTS
  // ============================================

  /**
   * Mark messages as read in a conversation
   * Updates unread count and triggers read receipt events
   *
   * @param {string} conversationId - Conversation ID
   * @param {string} messageId - Specific message ID (optional, marks all if not provided)
   * @returns {Promise<Object>} Read confirmation
   */
  markAsRead: async (conversationId, messageId = null) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        `${MESSAGING_BASE}/conversations/${conversationId}/read`,
        messageId ? { messageId } : {}
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ============================================
  // TYPING INDICATORS
  // ============================================

  /**
   * Send typing start indicator
   * Auto-expires after 3 seconds
   *
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Typing confirmation
   */
  startTyping: async (conversationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${MESSAGING_BASE}/conversations/${conversationId}/typing/start`
      );

      return response.data;
    } catch (error) {
      // Silent fail for typing indicators
return null;
    }
  },

  /**
   * Send typing stop indicator
   *
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Typing stop confirmation
   */
  stopTyping: async (conversationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${MESSAGING_BASE}/conversations/${conversationId}/typing/stop`
      );

      return response.data;
    } catch (error) {
      // Silent fail for typing indicators
return null;
    }
  },

  // ============================================
  // BLOCKING & REPORTING
  // ============================================

  /**
   * Block a user in a conversation
   *
   * @param {string} conversationId - Conversation ID
   * @param {string} reason - Reason for blocking (optional)
   * @returns {Promise<Object>} Block confirmation
   */
  blockUser: async (conversationId, reason = "") => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${MESSAGING_BASE}/conversations/${conversationId}/block`,
        { reason }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Unblock a user in a conversation
   *
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Unblock confirmation
   */
  unblockUser: async (conversationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `${MESSAGING_BASE}/conversations/${conversationId}/block`
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Report a message
   *
   * @param {string} messageId - Message ID
   * @param {Object} reportData - Report data
   * @param {string} reportData.reason - Report reason ('spam', 'harassment', 'inappropriate', 'abuse', 'other')
   * @param {string} reportData.description - Additional description
   * @returns {Promise<Object>} Report confirmation
   */
  reportMessage: async (messageId, reportData) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${MESSAGING_BASE}/messages/${messageId}/report`,
        {
          reason: reportData.reason,
          description: reportData.description || "",
        }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ============================================
  // MESSAGE ACTIONS (Delete, Edit, React)
  // ============================================

  /**
   * Delete a message
   * Only sender can delete, within 24 hours
   *
   * @param {string} messageId - Message ID
   * @returns {Promise<Object>} Delete confirmation
   */
  deleteMessage: async (messageId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `${MESSAGING_BASE}/messages/${messageId}`
      );

      return response.data;
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error(
          "You can only delete your own messages within 24 hours"
        );
      }
      throw handleApiError(error);
    }
  },

  /**
   * Edit a message
   * Only sender can edit, within 15 minutes
   *
   * @param {string} messageId - Message ID
   * @param {string} content - New message content
   * @returns {Promise<Object>} Updated message
   */
  editMessage: async (messageId, content) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        `${MESSAGING_BASE}/messages/${messageId}`,
        { content: content.trim() }
      );

      return response.data;
    } catch (error) {
      if (error.response?.status === 403) {
        throw new Error(
          "You can only edit your own messages within 15 minutes"
        );
      }
      throw handleApiError(error);
    }
  },

  /**
   * Add a reaction to a message
   *
   * @param {string} messageId - Message ID
   * @param {string} emoji - Emoji reaction
   * @returns {Promise<Object>} Reaction confirmation
   */
  addReaction: async (messageId, emoji) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${MESSAGING_BASE}/messages/${messageId}/react`,
        { emoji }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Remove a reaction from a message
   *
   * @param {string} messageId - Message ID
   * @param {string} emoji - Emoji reaction to remove
   * @returns {Promise<Object>} Removal confirmation
   */
  removeReaction: async (messageId, emoji) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `${MESSAGING_BASE}/messages/${messageId}/react`,
        { data: { emoji } }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ============================================
  // SEARCH
  // ============================================

  /**
   * Search messages across all or specific conversations
   * ⭐ ENHANCED: Now returns messageId and conversationId for jump-to-message
   *
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.query - Search query
   * @param {string} searchParams.conversationId - Limit search to specific conversation (optional)
   * @param {number} searchParams.page - Page number
   * @param {number} searchParams.limit - Items per page
   * @returns {Promise<Object>} Search results with pagination (includes messageId, conversationId)
   */
  searchMessages: async (searchParams) => {
    try {
      const {
        query,
        conversationId = null,
        page = 1,
        limit = 20,
      } = searchParams;

      const params = {
        query,
        page,
        limit,
      };

      if (conversationId) {
        params.conversationId = conversationId;
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(`${MESSAGING_BASE}/search`, {
        params,
      });

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ============================================
  // BIDIRECTIONAL SCROLL & JUMP TO MESSAGE
  // ============================================

  /**
   * Get message context for jump-to-message feature (WhatsApp-style)
   * Loads N messages before + target message + N messages after
   *
   * @param {string} conversationId - Conversation ID
   * @param {string} messageId - Target message ID to jump to
   * @param {number} before - Number of messages to load before target (default: 25)
   * @param {number} after - Number of messages to load after target (default: 25)
   * @returns {Promise<Object>} Messages with target info and cursors for bidirectional scroll
   */
  getMessageContext: async (conversationId, messageId, before = 25, after = 25) => {
    try {
      if (!conversationId) {
        throw new Error("Conversation ID is required");
      }
      if (!messageId) {
        throw new Error("Message ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${MESSAGING_BASE}/conversations/${conversationId}/messages/${messageId}/context`,
        {
          params: { before, after },
        }
      );

      return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  },

  /**
   * Load more messages from a specific position (bidirectional scroll)
   * Supports loading both OLDER (scroll up) and NEWER (scroll down) messages
   *
   * @param {string} conversationId - Conversation ID
   * @param {string} cursor - ISO timestamp cursor from previous response
   * @param {string} direction - Direction: 'older' (scroll up) or 'newer' (scroll down)
   * @param {number} limit - Number of messages to fetch (default: 50)
   * @returns {Promise<Object>} Messages with pagination info
   */
  loadMoreMessages: async (conversationId, cursor, direction = "older", limit = 50) => {
    try {
      if (!conversationId) {
        throw new Error("Conversation ID is required");
      }
      if (!cursor) {
        throw new Error("Cursor is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        `${MESSAGING_BASE}/conversations/${conversationId}/messages/load-more`,
        {
          params: {
            cursor,
            direction,
            limit,
          },
        }
      );

      return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  },

  // ============================================
  // FILE UPLOAD
  // ============================================

  /**
   * Upload a file in a conversation
   *
   * @param {string} conversationId - Conversation ID
   * @param {File} file - File to upload
   * @returns {Promise<Object>} Uploaded file message
   */
  uploadFile: async (conversationId, file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        `${MESSAGING_BASE}/conversations/${conversationId}/files`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ============================================
  // ARCHIVE & MUTE (Future Features)
  // ============================================

  /**
   * Archive a conversation
   *
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Archive confirmation
   */
  archiveConversation: async (conversationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        `${MESSAGING_BASE}/conversations/${conversationId}/archive`
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Unarchive a conversation
   *
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Unarchive confirmation
   */
  unarchiveConversation: async (conversationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `${MESSAGING_BASE}/conversations/${conversationId}/archive`
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Mute a conversation
   *
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Mute confirmation
   */
  muteConversation: async (conversationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        `${MESSAGING_BASE}/conversations/${conversationId}/mute`
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Unmute a conversation
   *
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} Unmute confirmation
   */
  unmuteConversation: async (conversationId) => {
    try {
      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        `${MESSAGING_BASE}/conversations/${conversationId}/mute`
      );

      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export default messagingService;
